import { CalendarClock, MicOff, Pause, Play } from "lucide-react";
import { Fragment, useEffect, useRef } from "react";

import { CrestedName } from "@/components/home/host-names";
import { Image } from "@/components/image";
import { Link } from "@/components/link";
import { usePlayer } from "@/components/providers/player-provider";
import { dummyIcon } from "@/lib/consts";
import type { PublicEpisode, PublicParticipant } from "@/lib/explore";
import {
  HOVER_DELAY_MS,
  warmForHover,
  warmForViewport,
} from "@/lib/preload-audio";
import { cn, formatDate } from "@/lib/utils";

/** The public display name — see `PublicParticipant`. */
const displayName = (participant: PublicParticipant) =>
  participant.firstName.trim();

/**
 * Only hosts appear on the card. Other participants and their roles stay in
 * the search index and on the episode detail page, but as card metadata they
 * were noise.
 */
function Participation({
  participants,
}: {
  participants: PublicParticipant[];
}) {
  const hosts = participants.filter(
    (participant) => participant.roleName === "Host"
  );

  if (hosts.length === 0) {
    return null;
  }

  return (
    <p className="line-clamp-1 text-sm text-muted-foreground">
      By{" "}
      {hosts.map((host, index) => (
        <Fragment key={host.contributorId}>
          {index > 0 && ", "}
          <Link
            className="transition-colors hover:text-primary hover:underline"
            href={`/view/people/${host.contributorId}`}
          >
            <CrestedName house={host.house} name={displayName(host)} />
          </Link>
        </Fragment>
      ))}
    </p>
  );
}

const startsInFuture = (startsAt: string, now = Date.now()) =>
  new Date(startsAt).getTime() > now;

export function EpisodeCard({
  episode,
  priority,
}: {
  episode: PublicEpisode;
  priority?: boolean;
}) {
  const { current, playRecording, playing, toggle } = usePlayer();
  const url = episode.recordingUrl;
  const isCurrent = current?.kind === "recording" && current.url === url;
  const isPlaying = isCurrent && playing;
  const isUpcoming = startsInFuture(episode.startsAt);

  const card = useRef<HTMLElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverAbort = useRef<AbortController | null>(null);

  // Opening the connection while the card is on screen means pressing play
  // does not start from a cold edge cache.
  useEffect(() => {
    const node = card.current;
    if (!node || !url) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        warmForViewport(url);
        observer.disconnect();
      }
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [url]);

  const cancelHoverWarm = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    hoverAbort.current?.abort();
    hoverAbort.current = null;
  };

  // Delayed so a pointer crossing the grid does not pull a chunk per card.
  const handlePointerEnter = () => {
    if (!url) {
      return;
    }
    cancelHoverWarm();
    hoverTimer.current = setTimeout(() => {
      hoverAbort.current = new AbortController();
      warmForHover(url, hoverAbort.current.signal);
    }, HOVER_DELAY_MS);
  };

  useEffect(() => cancelHoverWarm, []);

  const handlePlay = () => {
    if (!url) {
      return;
    }

    if (isCurrent) {
      toggle();
      return;
    }

    playRecording({
      episodeId: episode.id,
      iconUrl: episode.iconUrl ?? dummyIcon,
      showName: episode.showName,
      title: episode.title,
      url,
    });
  };

  return (
    <article
      className="group flex flex-col"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={cancelHoverWarm}
      ref={card}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-muted shadow-sm">
        <Image
          fill
          src={episode.iconUrl ?? dummyIcon}
          alt={`${episode.title} art`}
          className="object-cover"
          priority={priority}
          sizes="(min-width: 1280px) 16vw, (min-width: 640px) 33vw, 50vw"
        />

        {url ? (
          <button
            aria-label={
              isPlaying ? `Pause ${episode.title}` : `Play ${episode.title}`
            }
            className={cn(
              "absolute inset-0 m-auto flex size-12 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-[background-color,opacity] hover:bg-primary focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              isPlaying ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            )}
            onClick={handlePlay}
            type="button"
          >
            {isPlaying ? (
              <Pause className="size-5 fill-current" />
            ) : (
              <Play className="size-5 translate-x-0.5 fill-current" />
            )}
          </button>
        ) : (
          <span className="absolute top-2.5 start-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm backdrop-blur-sm">
            {isUpcoming ? (
              <CalendarClock aria-hidden className="size-3.5 text-primary" />
            ) : (
              <MicOff aria-hidden className="size-3.5 text-muted-foreground" />
            )}
            {isUpcoming ? "Upcoming" : "Not recorded"}
          </span>
        )}
      </div>

      {/* The show name leads: it is what listeners recognise. The generated
          episode title ("Ep02 : 18th Nov 2025") duplicated the date, so the
          date line doubles as the link to the episode instead. */}
      <div className="mt-3 flex flex-1 flex-col gap-1">
        <Link href={`/view/shows/${episode.showId}`}>
          <h3 className="line-clamp-2 font-semibold leading-snug hover:underline">
            {episode.showName}
          </h3>
        </Link>
        <Link
          className="text-sm text-muted-foreground hover:underline"
          href={`/view/episodes/${episode.id}`}
        >
          {formatDate(new Date(episode.startsAt))}
        </Link>
        <Participation participants={episode.participants} />
      </div>
    </article>
  );
}
